import React from 'react';
import { Topic } from '../types';

interface LessonViewProps {
  selectedTopic: Topic | null;
  lessonContent: string;
  aiContent?: string;
  practicalActivities: any[];
  developedCompetences: string[];
  loading: boolean;
  aiGenerating: boolean;
  setCurrentView: (view: 'topics') => void;
  generateAIExplanation: () => void;
  startQuiz: () => void;
}

export const LessonView: React.FC<LessonViewProps> = ({
  selectedTopic,
  lessonContent,
  aiContent,
  practicalActivities,
  developedCompetences,
  loading,
  aiGenerating,
  setCurrentView,
  generateAIExplanation,
  startQuiz
}) => {
  if (!selectedTopic) return null;

  return (
    <div style={{
      background: 'white',
      borderRadius: '16px',
      padding: '40px 60px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
      maxWidth: '1000px',
      margin: '0 auto',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
    }}>
      <style>
        {`
          .modern-content {
            font-size: 16px;
            line-height: 1.7;
            color: #374151;
            margin-top: 30px;
          }
          .modern-content::after {
            content: "";
            display: table;
            clear: both;
          }
          .modern-content h3 {
            font-size: 20px;
            font-weight: 700;
            color: #111827;
            margin-top: 32px;
            margin-bottom: 16px;
            clear: left;
          }
          .modern-content p {
            margin-bottom: 20px;
          }
          /* This simulates the right-aligned Zambian Example card */
          .modern-content .investigation, .modern-content .activity, .modern-content .visual-aid {
            float: right;
            width: 45%;
            margin-left: 40px;
            margin-bottom: 30px;
            background: #fffbeb; /* light yellow/beige */
            border: 2px solid #3b82f6;
            border-radius: 16px;
            padding: 24px;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          }
          .modern-content .investigation::before, .modern-content .activity::before {
            content: "Zambian Context";
            display: block;
            font-weight: bold;
            font-size: 18px;
            color: #111827;
            margin-bottom: 12px;
          }
          .modern-content ul {
            margin-bottom: 20px;
            padding-left: 20px;
          }
          .modern-content li {
            margin-bottom: 8px;
          }
          .modern-content img, .modern-content svg {
            width: 100%;
            border-radius: 8px;
            margin-top: 16px;
          }
          @media (max-width: 768px) {
            .modern-content .investigation, .modern-content .activity, .modern-content .visual-aid {
              float: none;
              width: 100%;
              margin-left: 0;
              margin-top: 24px;
              box-sizing: border-box;
            }
          }
        `}
      </style>

      <button
        onClick={() => setCurrentView('topics')}
        style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: '14px', cursor: 'pointer', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '4px' }}
      >
        ← Back to Topics
      </button>

      {/* Header matching preview */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid #e5e7eb', paddingBottom: '24px', marginBottom: '32px' }}>
        <div>
          <h1 style={{
            fontSize: '36px',
            fontWeight: '800',
            color: '#111827',
            margin: '8px 0 0 0',
            lineHeight: '1.2'
          }}>
            {selectedTopic.subject}: {selectedTopic.title}
          </h1>
        </div>
      </div>

      {/* Main Content Area */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <div style={{ width: '40px', height: '40px', border: '3px solid #e5e7eb', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto' }}></div>
          <p style={{ marginTop: '16px', color: '#6b7280' }}>Opening lesson...</p>
        </div>
      ) : (
        <div
          className="modern-content"
          dangerouslySetInnerHTML={{ __html: lessonContent }}
        />
      )}

      {/* AI Content Area */}
      {aiContent && (
        <div style={{ marginTop: '40px', paddingTop: '40px', borderTop: '2px dashed #3b82f6', clear: 'both' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <span style={{ fontSize: '24px' }}>🤖</span>
            <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1d4ed8', margin: 0 }}>Simplified AI Explanation</h2>
          </div>
          <div
            className="modern-content"
            dangerouslySetInnerHTML={{ __html: aiContent }}
          />
        </div>
      )}

      {/* Generate AI Explanation Button */}
      <div style={{ marginTop: '30px', clear: 'both', background: '#f8fafc', padding: '24px', borderRadius: '12px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
        <p style={{ margin: '0 0 16px 0', color: '#475569', fontSize: '15px' }}>
          {aiContent ? 'Not satisfied with this explanation? Get another perspective!' : 'Finding this a bit complex? Let our AI teacher simplify it for you!'}
        </p>
        <button
          onClick={generateAIExplanation}
          disabled={aiGenerating}
          style={{ padding: '12px 24px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: aiGenerating ? 'not-allowed' : 'pointer', opacity: aiGenerating ? 0.6 : 1, transition: 'background 0.2s' }}
        >
          {aiGenerating ? '🔄 Generating...' : (aiContent ? '🔄 Request Another Explanation' : '✨ Simplify Explanation')}
        </button>
      </div>

      {/* Separator */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        margin: '60px 0 40px 0',
        color: '#9ca3af',
        fontSize: '12px',
        textTransform: 'uppercase',
        letterSpacing: '1px'
      }}>
        <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }}></div>
        <span style={{ padding: '0 16px' }}>separator</span>
        <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }}></div>
      </div>

      {/* Embedded Quiz Section (Mockup styling to match image) */}
      <div style={{ clear: 'both' }}>
        <span style={{ fontSize: '14px', color: '#6b7280' }}>Interactive Quiz</span>
        <h2 style={{ fontSize: '28px', fontWeight: '700', color: '#111827', margin: '4px 0 24px 0' }}>
          Test Your Understanding
        </h2>

        <div style={{ marginBottom: '24px' }}>
          <p style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '20px' }}>
            Ready to test what you've learned about {selectedTopic.title}?
          </p>

          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <button
              onClick={() => startQuiz()}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '16px 24px',
                background: '#f0fdf4',
                border: '2px solid #22c55e',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '500',
                color: '#111827',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', borderRadius: '50%', border: '1px solid #22c55e', color: '#22c55e', fontWeight: 'bold' }}>A</span>
              Start Interactive Quiz
            </button>
            <button
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '16px 24px',
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                color: '#4b5563',
                cursor: 'not-allowed'
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', borderRadius: '50%', border: '1px solid #d1d5db', color: '#6b7280', fontWeight: 'bold' }}>B</span>
              Review Notes First
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px', marginTop: '32px' }}>
          <button
            onClick={() => startQuiz()}
            style={{ padding: '12px 24px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '6px', fontWeight: '500', fontSize: '14px', cursor: 'pointer' }}
          >
            Start Quiz →
          </button>
        </div>
      </div>

    </div>
  );
};
